import React, { useCallback, useMemo } from "react";
import classnames from "classnames";
import { useDebouncedFunction } from "src/shared/hooks";
import { SortTable } from "src/shared/interfaces";

import { TableProperty } from "./interface";
import { Head } from "./Head";
import { Row } from "./Row";


import "./index.scss";
import { CheckboxInput } from '../Common';

export interface TableProps<T> extends SortTable {
  items: T[];
  properties: TableProperty<T>[];
  onFinishScroll?: () => void;
  onRowClick?: (item: T) => void;
  selectableRows?: boolean;
  keyForSelect?: keyof T;
  selectedItems?: T[];
  onChangeSelectedItems?: (items: T[]) => void;
}

function Table<T>(props: TableProps<T>) {
  const {
    sortOrder,
    sortBy,
    onChangeSorting,
    items,
    properties,
    onFinishScroll,
    onRowClick,
    selectedItems,
    onChangeSelectedItems,
    selectableRows,
    keyForSelect,
  } = props;

  const debouncedFinishScroll = useDebouncedFunction(onFinishScroll);
  const key = useMemo(() => keyForSelect || properties[0].key, [keyForSelect, properties]);

  const onScroll = useCallback(
    (event: React.SyntheticEvent<Element>) => {
      const maxScroll = event.currentTarget.scrollHeight - event.currentTarget.clientHeight;
      if (event.currentTarget.scrollTop >= maxScroll - 1 && debouncedFinishScroll) {
        debouncedFinishScroll();
      }
    },
    [debouncedFinishScroll],
  );

  const onSelectUnselectItem = useCallback(
    (item: T) => {
      if (!selectedItems?.find((selectedItem) => selectedItem[key] === item[key])) {
        onChangeSelectedItems?.([...(selectedItems || []), item]);
      } else {
        onChangeSelectedItems?.(selectedItems?.filter((selectedItem) => selectedItem[key] !== item[key]));
      }
    },
    [selectedItems, onChangeSelectedItems, key],
  );

  const selectColumn = useCallback<(key: keyof T) => TableProperty<T>>(
    (key) => ({
      title: "",
      key,
      appearClickBlock: (item: T) => {
        return (
          <CheckboxInput
            className="checkbox-show-onhover"
            value={selectedItems?.find((selectedItem) => selectedItem[key] === item[key]) ? "1" : ""}
            onChange={() => true}
          />
        );
      },
      cellClick: (item: T) => {
        onSelectUnselectItem?.(item);
      },
    }),
    [onSelectUnselectItem, selectedItems],
  );

  const tableProperties = useMemo(() => {
    return selectableRows ? [selectColumn(key), ...properties] : properties;
  }, [properties, selectableRows, selectColumn, key]);

  return (
    <div className="table-wrapper">
      <Head<T> properties={tableProperties} sortBy={sortBy} sortOrder={sortOrder} onChangeSorting={onChangeSorting} />
      <div className={classnames("table-content", { "table-clickable": onRowClick })} onScroll={onScroll}>
        {items.map((item, index) => (
          <Row<T> key={index} item={item} properties={tableProperties} onClick={() => onRowClick?.(item)} />
        ))}
      </div>
    </div>
  );
}

export default Table;
